import { useState, useEffect, useCallback } from 'react';
import toastNotifications from '../utils/toastNotifications';

/**
 * 🔔 React Hook for Toast Notifications
 * 
 * Provides easy interface to display and manage toast notifications
 * Automatically handles lifecycle and cleanup
 * Production-ready with safety checks
 */
export const useToast = () => {
  const [currentToast, setCurrentToast] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Memoized handlers to prevent re-subscriptions
  const handleShow = useCallback((toast) => {
    if (!toast) return;
    
    setCurrentToast(toast);
    setIsVisible(true);
  }, []);

  const handleHide = useCallback(() => {
    setIsVisible(false);
    // Keep toast data for exit animation, clear after animation completes
    const timeoutId = setTimeout(() => {
      setCurrentToast(null);
    }, 300);
    
    // Cleanup timeout if component unmounts
    return () => clearTimeout(timeoutId);
  }, []);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => setCurrentToast(null), 300);
  }, []);

  useEffect(() => {
    // Initialize toast system on first mount
    toastNotifications.initialize();

    // Safety check for window and emitter
    if (typeof window === 'undefined' || !window.mainBetToastEmitter) {
      return;
    }

    const emitter = window.mainBetToastEmitter;

    // Subscribe to events
    emitter.on('show', handleShow);
    emitter.on('hide', handleHide);

    // Cleanup function
    return () => {
      if (typeof window !== 'undefined' && window.mainBetToastEmitter) {
        window.mainBetToastEmitter.off('show', handleShow);
        window.mainBetToastEmitter.off('hide', handleHide);
      }
    };
  }, [handleShow, handleHide]);

  // Manual toast controls with safety checks
  const showTopMatch = useCallback(() => {
    try {
      toastNotifications.showTopMatch();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to show top match toast:', error);
      }
    }
  }, []);

  const showAccumulatorPrompt = useCallback(() => {
    try {
      toastNotifications.showAccumulatorPrompt();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to show accumulator prompt:', error);
      }
    }
  }, []);

  const showOddsChange = useCallback((match, oldOdds, newOdds) => {
    try {
      toastNotifications.showOddsChange(match, oldOdds, newOdds);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to show odds change toast:', error);
      }
    }
  }, []);

  const showNewBonus = useCallback((bonus) => {
    try {
      toastNotifications.showNewBonus(bonus);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to show bonus toast:', error);
      }
    }
  }, []);

  const disableAll = useCallback(() => {
    try {
      toastNotifications.disable();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to disable toasts:', error);
      }
    }
  }, []);

  const disableType = useCallback((typeId) => {
    try {
      toastNotifications.disableType(typeId);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to disable toast type:', error);
      }
    }
  }, []);

  return {
    currentToast,
    isVisible,
    showTopMatch,
    showAccumulatorPrompt,
    showOddsChange,
    showNewBonus,
    dismiss,
    disableAll,
    disableType
  };
}; 